import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Building2, User, Save, Loader2, ExternalLink, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const emptyDealer = {
  display_name: "",
  tagline: "",
  bio: "",
  city: "",
  county: "",
  address: "",
  website: "",
  opening_hours: "",
  public_enabled: true,
  show_phone: true,
  show_email: false,
};

export function SellerProfileSection({ name, phone, onNamePhoneChange, onSaved }) {
  const { user, updateProfile } = useAuth();
  const [sellerType, setSellerType] = useState(user?.seller_type || "private");
  const [dealer, setDealer] = useState(emptyDealer);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setSellerType(user?.seller_type || "private");
    setDealer({ ...emptyDealer, ...(user?.dealer_profile || {}) });
  }, [user?.seller_type, user?.dealer_profile]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        phone: phone.trim(),
        seller_type: sellerType,
      };
      if (sellerType === "dealer") {
        payload.dealer_profile = {
          ...dealer,
          display_name: (dealer.display_name || name).trim(),
        };
      }
      await updateProfile(payload);
      toast.success("Profil mentve");
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Mentés sikertelen");
    } finally {
      setSaving(false);
    }
  };

  const publicPath = user?.public_url_hint || (user?.seller_slug ? `/kereskedes/${user.seller_slug}` : null);

  return (
    <form onSubmit={save} data-testid="seller-profile-section" className="space-y-5">
      <div>
        <div className="text-xs uppercase tracking-wide text-slate-400 mb-2">Eladói típus</div>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            data-testid="seller-type-private"
            onClick={() => setSellerType("private")}
            className={`rounded-xl p-3 border text-left text-sm transition-all ${
              sellerType === "private"
                ? "border-electric bg-electric/10"
                : "border-white/10 bg-white/[0.02] hover:bg-white/[0.04]"
            }`}
          >
            <User className="w-4 h-4 text-electric mb-1.5" />
            <div className="font-medium">Magán eladó</div>
            <div className="text-[10px] text-slate-400 mt-0.5">Nincs nyilvános profiloldal</div>
          </button>
          <button
            type="button"
            data-testid="seller-type-dealer"
            onClick={() => setSellerType("dealer")}
            className={`rounded-xl p-3 border text-left text-sm transition-all ${
              sellerType === "dealer"
                ? "border-electric bg-electric/10"
                : "border-white/10 bg-white/[0.02] hover:bg-white/[0.04]"
            }`}
          >
            <Building2 className="w-4 h-4 text-electric mb-1.5" />
            <div className="font-medium">Kereskedés</div>
            <div className="text-[10px] text-slate-400 mt-0.5">Publikus profil + készlet</div>
          </button>
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <label className="text-xs uppercase tracking-wide text-slate-400 block mb-2">Név</label>
          <input
            data-testid="profile-name"
            value={name}
            onChange={(e) => onNamePhoneChange({ name: e.target.value, phone })}
            className="w-full bg-white/[0.03] border border-white/10 rounded-xl p-3 text-sm focus:border-electric outline-none"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wide text-slate-400 block mb-2">Telefonszám</label>
          <input
            data-testid="profile-phone"
            value={phone}
            onChange={(e) => onNamePhoneChange({ name, phone: e.target.value })}
            className="w-full bg-white/[0.03] border border-white/10 rounded-xl p-3 text-sm focus:border-electric outline-none"
          />
        </div>
      </div>

      {sellerType === "dealer" && (
        <div className="space-y-4 pt-2 border-t border-white/5" data-testid="dealer-profile-fields">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="text-xs uppercase tracking-wide text-slate-400">Kereskedői adatok</div>
            {publicPath && dealer.public_enabled && (
              <Link
                to={publicPath}
                target="_blank"
                data-testid="dealer-public-preview"
                className="inline-flex items-center gap-1 text-[11px] text-electric hover:underline"
              >
                <ExternalLink className="w-3 h-3" /> Nyilvános oldal
              </Link>
            )}
          </div>
          <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
            <input
              type="checkbox"
              checked={dealer.public_enabled}
              onChange={(e) => setDealer({ ...dealer, public_enabled: e.target.checked })}
            />
            {dealer.public_enabled ? <Eye className="w-3.5 h-3.5 text-emerald-400" /> : <EyeOff className="w-3.5 h-3.5" />}
            Publikus kereskedői profil (bárki megnézheti)
          </label>
          <input
            placeholder="Megjelenő név (pl. Auto Park Premium)"
            value={dealer.display_name}
            onChange={(e) => setDealer({ ...dealer, display_name: e.target.value })}
            className="w-full bg-white/[0.03] border border-white/10 rounded-xl p-3 text-sm"
          />
          <input
            placeholder="Rövid szlogen"
            value={dealer.tagline}
            onChange={(e) => setDealer({ ...dealer, tagline: e.target.value })}
            className="w-full bg-white/[0.03] border border-white/10 rounded-xl p-3 text-sm"
          />
          <textarea
            placeholder="Bemutatkozás — mit kínálsz, garancia, szerviz…"
            rows={4}
            value={dealer.bio}
            onChange={(e) => setDealer({ ...dealer, bio: e.target.value })}
            className="w-full bg-white/[0.03] border border-white/10 rounded-xl p-3 text-sm resize-y min-h-[88px]"
          />
          <div className="grid grid-cols-2 gap-2">
            <input
              placeholder="Város"
              value={dealer.city}
              onChange={(e) => setDealer({ ...dealer, city: e.target.value })}
              className="bg-white/[0.03] border border-white/10 rounded-xl p-3 text-sm"
            />
            <input
              placeholder="Megye"
              value={dealer.county}
              onChange={(e) => setDealer({ ...dealer, county: e.target.value })}
              className="bg-white/[0.03] border border-white/10 rounded-xl p-3 text-sm"
            />
          </div>
          <input
            placeholder="Cím (opcionális)"
            value={dealer.address}
            onChange={(e) => setDealer({ ...dealer, address: e.target.value })}
            className="w-full bg-white/[0.03] border border-white/10 rounded-xl p-3 text-sm"
          />
          <input
            placeholder="Weboldal (https://…)"
            value={dealer.website}
            onChange={(e) => setDealer({ ...dealer, website: e.target.value })}
            className="w-full bg-white/[0.03] border border-white/10 rounded-xl p-3 text-sm"
          />
          <input
            placeholder="Nyitvatartás (pl. H–P 9–18)"
            value={dealer.opening_hours}
            onChange={(e) => setDealer({ ...dealer, opening_hours: e.target.value })}
            className="w-full bg-white/[0.03] border border-white/10 rounded-xl p-3 text-sm"
          />
          <div className="flex flex-wrap gap-4 text-xs text-slate-400">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={dealer.show_phone}
                onChange={(e) => setDealer({ ...dealer, show_phone: e.target.checked })}
              />
              Telefon a nyilvános oldalon
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={dealer.show_email}
                onChange={(e) => setDealer({ ...dealer, show_email: e.target.checked })}
              />
              Email a nyilvános oldalon
            </label>
          </div>
        </div>
      )}

      <button
        type="submit"
        data-testid="profile-save"
        disabled={saving}
        className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-full bg-electric hover:bg-electric-light disabled:opacity-50 text-white text-sm font-medium"
      >
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
        Mentés
      </button>
    </form>
  );
}
